import { DataTableColumnHeader, StatusChip, UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { formatToilDate } from '@/modules/toil/hr-my-toil/utils/toil-date';
import { DotsThreeVertical } from '@phosphor-icons/react';
import { ColumnDef } from '@tanstack/react-table';
import { ToilRequest } from '../../types/toil-manager.types';

/** Data written before this categorization existed has no `ToilCategory` — treated as
 * `WeekendToil` here too, matching the legacy `ToilCalculationHelper`'s fallback for old rows. */
const categoryLabelKey = (category?: ToilRequest['ToilCategory']): string =>
  category === 'SpecialToil' ? 'SPECIAL_TOIL' : 'WEEKEND_TOIL';

export type ToilRequestAction = 'Approved' | 'Rejected' | 'Archive' | 'Delete';

interface CreateToilRequestsTableColumnsProps {
  t: (key: string) => string;
  /** Opens the confirmation of the action picked from the row's menu. */
  onAction: (toilRequest: ToilRequest, action: ToilRequestAction) => void;
  canReview?: boolean;
  canArchive?: boolean;
  canDelete?: boolean;
  /** The signed-in user's id: nobody reviews or archives their own request (the TOIL workflows
   * refuse it too; hiding the items only spares a refusal). */
  currentUserId?: string;
  /** The departments the user may see (`useDepartmentScope().departments`), for the Department
   * column's filter. */
  departments?: readonly { ItemId: string; Title: string }[];
}

/** The actions the menu offers for a request: approve/reject while pending, archive once decided. */
const actionsFor = (
  request: ToilRequest,
  { canReview, canArchive, canDelete, currentUserId }: CreateToilRequestsTableColumnsProps
): ToilRequestAction[] => {
  const isOwn = Boolean(currentUserId) && request.UserId === currentUserId;
  const isPending = request.Status === 'Pending';
  return [
    ...(canReview && isPending && !isOwn ? (['Approved', 'Rejected'] as const) : []),
    ...(canArchive && !isPending && !isOwn && !request.IsArchived ? (['Archive'] as const) : []),
    ...(canDelete ? (['Delete'] as const) : []),
  ];
};

const ACTION_LABEL: Record<ToilRequestAction, string> = {
  Approved: 'APPROVE',
  Rejected: 'REJECT',
  Archive: 'ARCHIVE',
  Delete: 'DELETE',
};

export const createToilRequestsTableColumns = (
  props: CreateToilRequestsTableColumnsProps
): ColumnDef<ToilRequest>[] => {
  const { t, onAction, canReview, canArchive, canDelete, departments = [] } = props;
  return [
    {
      id: 'DisplayName',
      // The card has the name as its title and the email in its body instead.
      meta: { filter: { type: 'text' }, mobile: 'hidden' },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
      accessorFn: (row) => row.DisplayName ?? '',
      cell: ({ row }) => (
        <UserNameCell name={row.original.DisplayName} email={row.original.Email} />
      ),
    },
    {
      id: 'CardDisplayName',
      enableSorting: false,
      meta: { mobile: 'title', cardOnly: true },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
      accessorFn: (row) => row.DisplayName || '-',
    },
    {
      // Card body, two per line (as the correction requests' card): TOIL Date | Status,
      // Email | Department, Category | Type; the reason in the footer.
      id: 'CardEmail',
      enableSorting: false,
      meta: { mobile: 'body', mobileOrder: 2, cardOnly: true },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMAIL')} />,
      accessorFn: (row) => row.Email || '-',
      cell: ({ getValue }) => <span className="break-all">{getValue() as string}</span>,
    },
    {
      // Sorted and filtered by the department name stored on the request (the column id is the field
      // both use); the filter is a dropdown of the departments the user may see, and the list's
      // department scope (by id) still applies on top.
      id: 'DepartmentName',
      meta: {
        mobile: 'body',
        mobileOrder: 3,
        filter: {
          type: 'options',
          dropdown: true,
          options: departments.map((department) => ({
            value: department.Title,
            label: department.Title,
          })),
        },
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
      accessorFn: (row) => row.DepartmentName || '-',
    },
    {
      id: 'ToilDate',
      meta: { filter: { type: 'date' }, mobileOrder: 0 },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOIL_DATE')} />,
      accessorFn: (row) => row.ToilDate,
      cell: ({ row }) => formatToilDate(row.original),
    },
    {
      id: 'ToilCategory',
      meta: {
        mobileOrder: 4,
        filter: {
          type: 'options',
          options: [
            { value: 'WeekendToil', label: t('WEEKEND_TOIL') },
            { value: 'SpecialToil', label: t('SPECIAL_TOIL') },
          ],
        },
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOIL_CATEGORY')} />,
      accessorFn: (row) => row.ToilCategory,
      cell: ({ row }) => (
        <StatusChip tone={row.original.ToilCategory === 'SpecialToil' ? 'info' : 'muted'}>
          {t(categoryLabelKey(row.original.ToilCategory))}
        </StatusChip>
      ),
    },
    {
      id: 'ToilType',
      meta: {
        mobileOrder: 5,
        filter: {
          type: 'options',
          options: [
            { value: 'FullDay', label: t('FULL_DAY') },
            { value: 'HalfDay', label: t('HALF_DAY') },
          ],
        },
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOIL_TYPE')} />,
      accessorFn: (row) => row.ToilType,
      cell: ({ row }) => (row.original.ToilType === 'FullDay' ? t('FULL_DAY') : t('HALF_DAY')),
    },
    {
      id: 'Reason',
      // Card: in the footer, as in the old portal.
      meta: { filter: { type: 'text' }, mobile: 'footer' },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('REASON')} />,
      accessorFn: (row) => row.Reason?.trim() || '-',
    },
    {
      id: 'Status',
      meta: {
        // Card: beside the TOIL date; the top right holds the actions menu.
        mobile: 'body',
        mobileOrder: 1,
        filter: {
          type: 'options',
          options: ['Pending', 'Approved', 'Rejected'].map((status) => ({
            value: status,
            label: t(status.toUpperCase()),
          })),
        },
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
      accessorFn: (row) => row.Status,
      cell: ({ row }) => (
        <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
      ),
    },
    ...(canReview || canArchive || canDelete
      ? [
          {
            id: 'actions',
            // Card: top right, as the correction requests' card.
            meta: { mobile: 'badge' as const },
            header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
            enableSorting: false,
            enableHiding: false,
            cell: ({ row }) => {
              const actions = actionsFor(row.original, props);
              if (actions.length === 0) return null;
              // Each item opens its confirmation; nothing happens from the menu itself.
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label={t('ACTION')}>
                      <DotsThreeVertical className="size-5" weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {actions.map((action) => (
                      <DropdownMenuItem
                        key={action}
                        className={
                          action === 'Rejected' || action === 'Delete'
                            ? 'cursor-pointer text-destructive'
                            : 'cursor-pointer'
                        }
                        onSelect={() => onAction(row.original, action)}
                      >
                        {t(ACTION_LABEL[action])}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          } satisfies ColumnDef<ToilRequest>,
        ]
      : []),
  ];
};
